import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Cancel, ExpandMore, Save } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { usePersistedSetting } from '@@common';

import DialogHeaderActions from '../../../components/DialogHeaderActions';
import JsonDisplayActions from './JsonDisplayActions';
import type { JsonDisplayMode } from './JsonDisplayTypes';
import JsonTreeView from './JsonTreeView';

interface JsonDialogProps {
	open: boolean;
	onClose: () => void;
	title: string;
	copied: boolean;
	onCopy: () => Promise<void>;
	editable: boolean;
	editing: boolean;
	onEdit: () => void;
	editedText: string;
	onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	jsonError: string | null;
	jsonString: string;
	saving: boolean;
	onCancel: () => void;
	onSave: () => Promise<void>;
	displayMode: JsonDisplayMode;
	onDisplayModeChange: (mode: JsonDisplayMode | ((prev: JsonDisplayMode) => JsonDisplayMode)) => void;
}

const JsonDialog = memo(function JsonDialog({
	open,
	onClose,
	title,
	copied,
	onCopy,
	editable,
	editing,
	onEdit,
	editedText,
	onTextChange,
	jsonError,
	jsonString,
	saving,
	onCancel,
	onSave,
	displayMode,
	onDisplayModeChange,
}: JsonDialogProps) {
	const [wrapText, setWrapText] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);
	const [expandAllSignal, setExpandAllSignal] = useState(0);
	const [collapseAllSignal, setCollapseAllSignal] = useState(0);
	useEffect(() => {
		if (open) {
			setIsMaximized(false);
		}
	}, [open]);
	const parsedData = useMemo(() => {
		try {
			return JSON.parse(jsonString);
		} catch {
			return null;
		}
	}, [jsonString]);
	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullScreen={isMaximized}
			maxWidth={false}
			fullWidth
			sx={{
				zIndex: 1500,
				'& .MuiDialog-paper': isMaximized
					? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', overflow: 'hidden' }
					: { width: 'min(960px, 95vw)', height: '70vh', maxHeight: '90vh', overflow: 'hidden' },
			}}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
					{title}
				</Typography>
				<Box sx={{ flex: 1 }} />
				<Stack direction="row" alignItems="center" spacing={1}>
					<JsonDisplayActions
						copied={copied}
						displayMode={displayMode}
						editable={editable}
						editing={editing}
						wrapText={wrapText}
						onCollapseAll={() => setCollapseAllSignal((value) => value + 1)}
						onCopy={onCopy}
						onEdit={onEdit}
						onExpandAll={() => setExpandAllSignal((value) => value + 1)}
						onToggleDisplayMode={() => onDisplayModeChange((mode) => (mode === 'json' ? 'tree' : 'json'))}
						onToggleWrap={() => setWrapText((value) => !value)}
					/>
					<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={onClose} />
				</Stack>
			</DialogTitle>
			<DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 2, height: '100%', bgcolor: 'background.default' }}>
				{editing ? (
					<TextField
						fullWidth
						multiline
						value={editedText}
						onChange={onTextChange}
						error={!!jsonError}
						helperText={jsonError}
						slotProps={{
							input: {
								style: {
									fontFamily: 'monospace',
									fontSize: '0.875rem',
									height: '100%',
								},
							},
						}}
						sx={{ flex: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' } }}
					/>
				) : displayMode === 'tree' && parsedData ? (
					<Box sx={{ overflow: 'auto', flex: 1, bgcolor: 'background.default' }}>
						<JsonTreeView data={parsedData} expandAllSignal={expandAllSignal} collapseAllSignal={collapseAllSignal} />
					</Box>
				) : (
					<Box
						component="pre"
						sx={{
							m: 0,
							p: 2,
							fontSize: '0.875rem',
							fontFamily: 'monospace',
							whiteSpace: wrapText ? 'pre-wrap' : 'pre',
							overflowX: wrapText ? 'hidden' : 'auto',
							bgcolor: 'background.default',
							borderRadius: 0,
							border: 'none',
							flex: 1,
						}}
					>
						{jsonString}
					</Box>
				)}
			</DialogContent>
			{editing && (
				<DialogActions>
					<Button onClick={onCancel} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={onSave} variant="contained" disabled={saving || !!jsonError}>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
});

// Stable empty fallback — avoids new reference on every render when data is absent
const EMPTY_DATA: unknown[] = [];

const GLOBAL_WRAP_KEY = 'jsonDataDisplay.wrapText';
const GLOBAL_VIEW_MODE_KEY = 'jsonDataDisplay.viewMode';

interface JsonDataDisplayProps {
	/** Title to display above the JSON */
	title: string;
	/** Data to be displayed as JSON */
	data?: any;
	/** Whether to display as an accordion (default: false) */
	asAccordion?: boolean;
	/** Whether the accordion is expanded by default (only used if asAccordion is true) */
	defaultExpanded?: boolean;
	/** Custom subtitle for accordion (e.g., "5 items") */
	accordionSubtitle?: string;
	/** Whether to show a divider above (default: false) */
	showDivider?: boolean;
	/** Whether to allow editing (default: false) */
	editable?: boolean;
	/** Callback when data is saved */
	onSave?: (data: any) => Promise<void>;
	/** Whether save is in progress */
	saving?: boolean;
	/** localStorage key prefix for persisting settings (e.g. word wrap). When omitted, a global key is used. */
	settingsKey?: string;
	/** localStorage key for persisting accordion expanded state. When omitted, accordion state is local to the component. */
	accordionExpandedStorageKey?: string;
}

function JsonDataDisplay({
	title,
	data = EMPTY_DATA,
	asAccordion = false,
	defaultExpanded = false,
	accordionSubtitle,
	showDivider = false,
	editable = false,
	onSave,
	saving = false,
	settingsKey,
	accordionExpandedStorageKey,
}: JsonDataDisplayProps) {
	const wrapStorageKey = settingsKey ? `${settingsKey}.wrapText` : GLOBAL_WRAP_KEY;
	const viewModeStorageKey = settingsKey ? `${settingsKey}.viewMode` : GLOBAL_VIEW_MODE_KEY;
	const [wrapText, setWrapText] = usePersistedSetting(wrapStorageKey, false);
	const [displayMode, setDisplayMode] = usePersistedSetting<JsonDisplayMode>(viewModeStorageKey, 'json');
	const [persistedAccordionExpanded, setPersistedAccordionExpanded] = usePersistedSetting(
		accordionExpandedStorageKey ?? 'jsonDataDisplay.accordionExpanded.unused',
		defaultExpanded,
	);

	const [copied, setCopied] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editedText, setEditedText] = useState('');
	const [jsonError, setJsonError] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogEditing, setDialogEditing] = useState(false);
	const [dialogEditedText, setDialogEditedText] = useState('');
	const [dialogJsonError, setDialogJsonError] = useState<string | null>(null);
	const [localAccordionExpanded, setLocalAccordionExpanded] = useState(defaultExpanded);
	const [expandAllSignal, setExpandAllSignal] = useState(0);
	const [collapseAllSignal, setCollapseAllSignal] = useState(0);
	const accordionExpanded = accordionExpandedStorageKey ? persistedAccordionExpanded : localAccordionExpanded;
	const setAccordionExpanded = accordionExpandedStorageKey ? setPersistedAccordionExpanded : setLocalAccordionExpanded;
	const jsonString = useMemo(() => JSON.stringify(data, null, 2), [data]);

	useEffect(() => {
		if (isEditing) {
			setEditedText(jsonString);
			setJsonError(null);
		}
		// Intentionally omit jsonString: we only want to reset editedText when
		// editing mode is toggled on, not on every data refresh while editing.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditing]);

	const isEmpty = !data || (typeof data === 'object' && Object.keys(data).length === 0);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(jsonString);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Failed to copy:', err);
		}
	}, [jsonString]);

	const handleEdit = useCallback(() => {
		setAccordionExpanded(true);
		setIsEditing(true);
	}, [setAccordionExpanded]);

	const handleCancel = useCallback(() => {
		setIsEditing(false);
		setEditedText('');
		setJsonError(null);
	}, []);

	const handleSave = useCallback(async () => {
		try {
			const parsedData = JSON.parse(editedText);
			if (onSave) {
				await onSave(parsedData);
				setIsEditing(false);
				setJsonError(null);
			}
		} catch (err) {
			if (err instanceof SyntaxError) {
				setJsonError(`Invalid JSON: ${err.message}`);
			} else {
				setJsonError('Failed to save');
			}
		}
	}, [editedText, onSave]);

	const handleTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setEditedText(event.target.value);
		setJsonError(null);
	}, []);

	const handleOpenDialog = useCallback(() => {
		setDialogOpen(true);
		setDialogEditing(false);
		setDialogEditedText(jsonString);
		setDialogJsonError(null);
	}, [jsonString]);

	const handleCloseDialog = useCallback(() => {
		setDialogOpen(false);
		setDialogEditing(false);
		setDialogEditedText('');
		setDialogJsonError(null);
	}, []);

	const handleDialogEdit = useCallback(() => {
		setDialogEditing(true);
		setDialogEditedText(jsonString);
	}, [jsonString]);

	const handleDialogCancel = useCallback(() => {
		setDialogEditing(false);
		setDialogEditedText(jsonString);
		setDialogJsonError(null);
	}, [jsonString]);

	const handleDialogSave = useCallback(async () => {
		try {
			const parsedData = JSON.parse(dialogEditedText);
			if (onSave) {
				await onSave(parsedData);
				setDialogEditing(false);
				setDialogJsonError(null);
				setDialogOpen(false);
			}
		} catch (err) {
			if (err instanceof SyntaxError) {
				setDialogJsonError(`Invalid JSON: ${err.message}`);
			} else {
				setDialogJsonError('Failed to save');
			}
		}
	}, [dialogEditedText, onSave]);

	const handleDialogTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setDialogEditedText(event.target.value);
		setDialogJsonError(null);
	}, []);

	const handleDialogCopy = useCallback(async () => {
		try {
			const textToCopy = dialogEditing ? dialogEditedText : jsonString;
			await navigator.clipboard.writeText(textToCopy);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Failed to copy:', err);
		}
	}, [dialogEditing, dialogEditedText, jsonString]);

	// Don't hide the component if it's editable, even when data is empty
	if (!editable && isEmpty) {
		return null;
	}

	const actionButtons = (
		<JsonDisplayActions
			asAccordion={asAccordion}
			copied={copied}
			displayMode={displayMode}
			editable={editable}
			showOpenInDialog
			stopPropagation={asAccordion}
			wrapText={wrapText}
			onCollapseAll={() => setCollapseAllSignal((value) => value + 1)}
			onCopy={handleCopy}
			onEdit={handleEdit}
			onExpandAll={() => setExpandAllSignal((value) => value + 1)}
			onOpenDialog={handleOpenDialog}
			onToggleDisplayMode={() => setDisplayMode((mode) => (mode === 'json' ? 'tree' : 'json'))}
			onToggleWrap={() => setWrapText((value) => !value)}
		/>
	);

	const jsonContent = isEditing ? (
		<Box>
			<TextField
				fullWidth
				multiline
				rows={15}
				value={editedText}
				onChange={handleTextChange}
				error={!!jsonError}
				helperText={jsonError}
				slotProps={{
					input: {
						style: {
							fontFamily: 'monospace',
							fontSize: '0.75rem',
						},
					},
				}}
				sx={{ mb: 2 }}
			/>
			<Stack direction="row" spacing={1} justifyContent="flex-end">
				<Button size="small" variant="outlined" startIcon={<Cancel />} onClick={handleCancel} disabled={saving}>
					Cancel
				</Button>
				<Button size="small" variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !!jsonError}>
					{saving ? 'Saving...' : 'Save'}
				</Button>
			</Stack>
		</Box>
	) : displayMode === 'tree' ? (
		<Box
			sx={{
				p: 1.5,
				bgcolor: asAccordion ? 'background.default' : 'action.hover',
				borderRadius: asAccordion ? 0 : 1,
				border: asAccordion ? 'none' : '1px solid',
				borderColor: 'divider',
				overflowX: 'auto',
			}}
		>
			<JsonTreeView data={data} expandAllSignal={expandAllSignal} collapseAllSignal={collapseAllSignal} />
		</Box>
	) : (
		<Box sx={{ position: 'relative' }}>
			<Box
				component="pre"
				sx={{
					m: 0,
					p: 1.5,
					pr: asAccordion ? 1.5 : editable ? 10 : 5,
					fontSize: '0.75rem',
					fontFamily: 'monospace',
					whiteSpace: wrapText ? 'pre-wrap' : 'pre',
					overflowX: wrapText ? 'hidden' : 'auto',
					bgcolor: asAccordion ? 'background.default' : 'action.hover',
					borderRadius: asAccordion ? 0 : 1,
					border: asAccordion ? 'none' : '1px solid',
					borderColor: 'divider',
				}}
			>
				{jsonString}
			</Box>
			{!asAccordion && <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 1 }}>{actionButtons}</Box>}
		</Box>
	);

	if (asAccordion) {
		const itemCount = typeof data === 'object' ? Object.keys(data).length : 0;
		const subtitle = accordionSubtitle || `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;

		return (
			<>
				<Accordion
					disableGutters
					elevation={0}
					expanded={accordionExpanded}
					onChange={(_, isExpanded) => setAccordionExpanded(isExpanded)}
					sx={{ '&:before': { display: 'none' } }}
				>
					<AccordionSummary
						expandIcon={<ExpandMore />}
						sx={{
							minHeight: '36px',
							'&.Mui-expanded': { minHeight: '36px' },
							'& .MuiAccordionSummary-content': { alignItems: 'center', minWidth: 0 },
							py: 0.5,
							px: 1.5,
							borderTop: showDivider ? '1px solid' : 'none',
							borderColor: 'divider',
						}}
					>
						<Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
							{title} ({subtitle})
						</Typography>
						<Box sx={{ flex: 1 }} />
						{actionButtons}
					</AccordionSummary>
					{accordionExpanded && <AccordionDetails sx={{ p: 1.5, bgcolor: 'background.default' }}>{jsonContent}</AccordionDetails>}
				</Accordion>
				<JsonDialog
					open={dialogOpen}
					onClose={handleCloseDialog}
					title={title}
					copied={copied}
					onCopy={handleDialogCopy}
					editable={editable}
					editing={dialogEditing}
					onEdit={handleDialogEdit}
					editedText={dialogEditedText}
					onTextChange={handleDialogTextChange}
					jsonError={dialogJsonError}
					jsonString={jsonString}
					saving={saving}
					onCancel={handleDialogCancel}
					onSave={handleDialogSave}
					displayMode={displayMode}
					onDisplayModeChange={setDisplayMode}
				/>
			</>
		);
	}

	return (
		<Box sx={{ mb: 2 }}>
			<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
				{title}
			</Typography>
			{jsonContent}
			<JsonDialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				title={title}
				copied={copied}
				onCopy={handleDialogCopy}
				editable={editable}
				editing={dialogEditing}
				onEdit={handleDialogEdit}
				editedText={dialogEditedText}
				onTextChange={handleDialogTextChange}
				jsonError={dialogJsonError}
				jsonString={jsonString}
				saving={saving}
				onCancel={handleDialogCancel}
				onSave={handleDialogSave}
				displayMode={displayMode}
				onDisplayModeChange={setDisplayMode}
			/>
		</Box>
	);
}

export default memo(JsonDataDisplay);
